import { observer } from "mobx-react-lite";
import { useExonnHubStore } from "src/admin/modules/b2b-gate-order/components/ExonnHub/useExonnHubStore.ts";
import { Collapsible } from "src/components/Collapsible/Collapsible.tsx";
import * as styles from "./Categories.style.ts";
import { MiniIconButton } from "src/components/MiniIconButton/MiniIconButton.tsx";
import type { ExonnHubCategory } from "@exonn/js-sdk";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { useQuery } from "@tanstack/react-query";
import type { MouseEvent } from "react";
import { ShowMore } from "@re-dev/react-truncate";
import { getExonnApi } from "src/exonn-api/exonn-api.tsx";

export const Categories = observer(() => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["exonn-hub", "categories"],
    queryFn: async ({ signal }) => {
      const api = getExonnApi();
      const response = await api.supplierProductHub.getCategories(null, null, {
        signal,
      });
      return response.data;
    },
    gcTime: 0,
    staleTime: 0,
  });
  const { t } = useTranslation(TranslationNamespaceEnum.ExonnHubComponent);

  const store = useExonnHubStore();

  const handleCollapsed = (collapsed: boolean) =>
    store.setCategoriesExpanded(!collapsed);

  return (
    <div css={styles.root}>
      <Collapsible
        onCollapsedChange={handleCollapsed}
        collapsed={!store.categoriesExpanded}
        title={t("categoriesGroup")}
      >
        {error ? (
          <LoadCategoriesError onTryAgain={refetch} />
        ) : isLoading ? (
          <LoadingCategories />
        ) : (
          data && <LoadedCategories categories={data} />
        )}
      </Collapsible>
    </div>
  );
});

const LoadCategoriesError = ({ onTryAgain }: { onTryAgain: () => void }) => {
  const { t } = useTranslation(TranslationNamespaceEnum.ExonnHubComponent);
  return (
    <div css={styles.errorContainer}>
      <div css={styles.errorText}>{t("loadingCategoriesFailure")}</div>
      <div css={styles.refetchButton} onClick={onTryAgain}>
        {t("actions.tryAgain", {
          ns: TranslationNamespaceEnum.Common,
        })}
      </div>
    </div>
  );
};

const LoadingCategories = () => {
  const { t } = useTranslation(TranslationNamespaceEnum.ExonnHubComponent);
  return <div css={styles.loadingContainer}>{t("loadingCategories")}</div>;
};

type Category = {
  id: string | undefined;
  title: string;
  path: string[];
  child: Category[];
};

const LoadedCategories = observer(
  ({ categories }: { categories: ExonnHubCategory[] }) => {
    const store = useExonnHubStore();
    const { t } = useTranslation(TranslationNamespaceEnum.ExonnHubComponent);

    const rootCategory: Category = {
      title: "",
      id: undefined,
      child: [
        {
          title: t("rootCategory"),
          id: undefined,
          path: [],
          child: [],
        } as unknown as ExonnHubCategory,
        ...categories,
      ],
      path: [],
    };

    const currentCategory =
      store.categoryPath.length > 0
        ? findCategory(categories, store.categoryPath)
        : rootCategory;

    const currentCategories = currentCategory?.child ?? [];

    const goBack = () => {
      store.categoryPath.pop();
    };

    const backCategoryTitle =
      store.categoryPath.length === 1
        ? t("allCategories")
        : currentCategory?.title;

    return (
      <div css={styles.list}>
        {store.categoryPath.length > 0 && (
          <div css={styles.backButton} onClick={goBack}>
            <i className={"icon-15-sb_arw_mn"} />
            <span css={styles.backButtonText}>{backCategoryTitle}</span>
          </div>
        )}

        {currentCategories.map((category) => (
          <Category category={category} />
        ))}
      </div>
    );
  },
);

const Category = observer(({ category }: { category: Category }) => {
  const store = useExonnHubStore();
  const activeCategory = store.activeCategory;

  const handleOpen = (e: MouseEvent) => {
    e.stopPropagation();
    store.setActiveCategory(category.id);
    if (category.child.length) {
      store.setCategoryPath(category.path);
    }
  };

  const handleOpenSubTree = () => {
    store.setCategoryPath(category.path);
  };

  return (
    <div css={styles.category} key={category.id}>
      <span
        css={styles.categoryInner({
          isActive: activeCategory === category.id,
        })}
        onClick={handleOpen}
      >
        {category.title === "Food" && (
          <i css={styles.categoryIcon} className={"icon-15-food"} />
        )}
        {category.title === "Non-Food" && (
          <i css={styles.categoryIcon} className={"icon-15-not-food"} />
        )}
        <span
          data-text=""
          css={styles.categoryText({
            isActive: activeCategory === category.id,
          })}
        >
          <ShowMore more={""} lines={1}>
            {category.title}
          </ShowMore>
        </span>
      </span>
      {category.child.length > 0 && (
        <MiniIconButton css={styles.categoryArrow} onClick={handleOpenSubTree}>
          <i className={"icon-15-arrw_r"} />
        </MiniIconButton>
      )}
    </div>
  );
});

const findCategory = (
  categories: ExonnHubCategory[],
  path: string[],
  level: number = 0,
) => {
  for (let i = 0; i < categories.length; i++) {
    const category = categories[i];
    if (category.id === path[level]) {
      if (path.length === level + 1) {
        return category;
      }
      return findCategory(category.child, path, level + 1);
    }
  }
  return undefined;
};
